/**
 * The journal entries (قيود اليومية), newest first: each with its lines -
 * the debit side, then the credit side - its total and currency.
 */

import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Button from "@mui/material/Button";
import { Table, TableBody, TableCell, TableHead, TableRow } from "@mui/material";

import SuiBox from "components/SuiBox";
import SuiTypography from "components/SuiTypography";
import CargoLoader from "components/CargoLoader";
import FilterBar, { filterRows } from "components/FilterBar";
import { FilterDates, inDateRange } from "components/FilterBar/controls";
import Pagination, { usePagination } from "components/Pagination";
import RowActions from "components/RowActions";

import JournalDialog from "./JournalDialog";
import { api, problemOf, money, docNo, byCurrency, currencyLine } from "./api";

const cell = { fontSize: 14, whiteSpace: "nowrap" };

/** One line as it reads: "123 السيارات   20,000.00". */
function Line({ line, side }) {
  return (
    // the amount sits right beside its account, not across the cell
    <SuiBox
      display="grid"
      gap={2}
      sx={{
        gridTemplateColumns: "minmax(0, 1fr) 110px",
        maxWidth: 480,
        fontSize: 13,
        paddingInlineStart: side === "credit" ? 3 : 0,
      }}
    >
      <span>
        {side === "credit" ? "إلى · " : "من · "}
        <b>{line.account_code}</b> {line.account_name}
        {line.note ? <span style={{ opacity: 0.6 }}>{` - ${line.note}`}</span> : null}
      </span>
      <span style={{ fontVariantNumeric: "tabular-nums", textAlign: "left" }}>
        {money(side === "credit" ? line.credit : line.debit)}
      </span>
    </SuiBox>
  );
}

Line.propTypes = {
  line: PropTypes.shape({
    account_code: PropTypes.string,
    account_name: PropTypes.string,
    debit: PropTypes.number,
    credit: PropTypes.number,
    note: PropTypes.string,
  }).isRequired,
  side: PropTypes.oneOf(["debit", "credit"]).isRequired,
};

function JournalTab({ onChanged }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  // {entry} to edit, {} for new, null when closed
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await api.get("/journal");
      setRows(
        (Array.isArray(data) ? data : []).map((e) => ({
          ...e,
          no: docNo("journal", e.number),
          // what the search box looks through
          accounts: (e.lines || []).map((l) => `${l.account_code} ${l.account_name}`).join(" "),
        }))
      );
    } catch (e) {
      setError(problemOf(e, "Could not load the journal"));
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const saved = () => {
    load();
    onChanged();
  };

  const remove = async (e) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Delete ${e.no} - ${money(e.total)} ${e.currency}?`)) return;
    try {
      await api.del(`/journal/${e.id}`);
      saved();
    } catch (err) {
      // eslint-disable-next-line no-alert
      window.alert(problemOf(err, "Could not delete"));
    }
  };

  const filtered = filterRows(
    rows.filter((r) => inDateRange(r.date, from, to)),
    query
  );
  const pager = usePagination(filtered);

  return (
    <>
      <FilterBar
        title="Journal Entries · قيود اليومية"
        value={query}
        onChange={setQuery}
        count={filtered.length}
        total={rows.length}
        placeholder="Search number, account, description..."
        actions={
          <>
            <SuiTypography variant="caption" sx={{ opacity: 0.75, whiteSpace: "nowrap" }}>
              {currencyLine(byCurrency(filtered, (r) => r.total))}
            </SuiTypography>
            <Button variant="contained" color="primary" onClick={() => setEditing({})}>
              New Entry · قيد جديد
            </Button>
          </>
        }
      >
        <FilterDates label="Date" from={from} to={to} onFrom={setFrom} onTo={setTo} />
      </FilterBar>

      {error && (
        <SuiBox px={3} pb={2}>
          <SuiTypography variant="button" color="error">
            {error}
          </SuiTypography>
        </SuiBox>
      )}

      {loading ? (
        <CargoLoader label="Loading the journal" />
      ) : (
        <>
          <SuiBox sx={{ overflowX: "auto" }}>
            <Table sx={{ minWidth: 960 }}>
              <TableHead>
                <TableRow>
                  <TableCell align="center">No.</TableCell>
                  <TableCell align="center">Date</TableCell>
                  <TableCell>Lines</TableCell>
                  <TableCell align="center">Description</TableCell>
                  <TableCell align="center">Total</TableCell>
                  <TableCell align="center">Operations</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {pager.pageRows.map((e) => (
                  <TableRow key={e.id}>
                    <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                      {e.no}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      {e.date}
                    </TableCell>
                    <TableCell sx={{ minWidth: 340 }} dir="rtl">
                      {(e.lines || [])
                        .filter((l) => l.debit > 0)
                        .map((l, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Line key={`d${i}`} line={l} side="debit" />
                        ))}
                      {(e.lines || [])
                        .filter((l) => l.credit > 0)
                        .map((l, i) => (
                          // eslint-disable-next-line react/no-array-index-key
                          <Line key={`c${i}`} line={l} side="credit" />
                        ))}
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontSize: 13, maxWidth: 240, whiteSpace: "normal" }}
                    >
                      {[e.description, e.reference].filter(Boolean).join(" · ") || "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ ...cell, fontWeight: 700 }}>
                      {`${money(e.total)} ${e.currency}`}
                    </TableCell>
                    <TableCell align="center" sx={cell}>
                      <RowActions
                        actions={[
                          {
                            icon: "edit",
                            label: "Edit",
                            onClick: () => setEditing({ entry: e }),
                          },
                          {
                            icon: "delete",
                            label: "Delete",
                            tone: "danger",
                            onClick: () => remove(e),
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))}
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                      <SuiTypography variant="button" color="text">
                        No entries yet - an entry moves the accounts nothing else moves: the fixed
                        assets, inventory, advances, capital
                      </SuiTypography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </SuiBox>
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <Pagination {...pager} />
        </>
      )}

      <JournalDialog
        open={Boolean(editing)}
        entry={editing?.entry || null}
        onClose={() => setEditing(null)}
        onSaved={saved}
      />
    </>
  );
}

JournalTab.propTypes = { onChanged: PropTypes.func.isRequired };

export default JournalTab;
